import { useRef, useEffect, useCallback, useState, forwardRef } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { Loader2 } from 'lucide-react';
import { isAllowedGlbOrigin } from '@/services/previewService';

interface ThreeDViewerProps {
  glbUrl: string | null;
  onResetRef?: React.MutableRefObject<(() => void) | null>;
  onZoomInRef?: React.MutableRefObject<(() => void) | null>;
  onLoadComplete?: () => void;
  onLoadError?: (error: string) => void;
}

const disposeMaterial = (mat: THREE.Material) => {
  for (const value of Object.values(mat)) {
    if (value instanceof THREE.Texture) {
      value.dispose();
    }
  }
  mat.dispose();
};

/** Zoom by adjusting camera distance from target (type-safe, no dollyIn/Out) */
function _zoomByDistance(
  controls: OrbitControls,
  camera: THREE.PerspectiveCamera,
  factor: number
) {
  const direction = new THREE.Vector3()
    .subVectors(camera.position, controls.target)
    .normalize();
  const currentDistance = camera.position.distanceTo(controls.target);
  const newDistance = THREE.MathUtils.clamp(
    currentDistance * (1 + factor),
    controls.minDistance,
    controls.maxDistance
  );
  camera.position.copy(controls.target).addScaledVector(direction, newDistance);
}

/** Rotate azimuth by manipulating camera position around target */
function _rotateAzimuth(
  controls: OrbitControls,
  camera: THREE.PerspectiveCamera,
  angle: number
) {
  const offset = new THREE.Vector3().subVectors(camera.position, controls.target);
  const spherical = new THREE.Spherical().setFromVector3(offset);
  spherical.theta += angle;
  offset.setFromSpherical(spherical);
  camera.position.copy(controls.target).add(offset);
}

/** Rotate polar by manipulating camera position around target */
function _rotatePolar(
  controls: OrbitControls,
  camera: THREE.PerspectiveCamera,
  angle: number
) {
  const offset = new THREE.Vector3().subVectors(camera.position, controls.target);
  const spherical = new THREE.Spherical().setFromVector3(offset);
  spherical.phi = THREE.MathUtils.clamp(
    spherical.phi + angle,
    controls.minPolarAngle,
    controls.maxPolarAngle
  );
  offset.setFromSpherical(spherical);
  camera.position.copy(controls.target).add(offset);
}

/** Auto-frame model: move camera to optimal product photography angle */
function autoFrameModel(
  model: THREE.Object3D,
  camera: THREE.PerspectiveCamera,
  controls: OrbitControls
): THREE.Vector3 {
  const box = new THREE.Box3().setFromObject(model);
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);

  const fov = camera.fov * (Math.PI / 180);
  const distance = (maxDim / (2 * Math.tan(fov / 2))) * 1.6;

  // Product photography angle: slightly elevated, slightly to the left
  const azimuth = -Math.PI / 8;
  const elevation = Math.PI / 8;

  const cameraPosition = new THREE.Vector3(
    center.x + distance * Math.sin(azimuth) * Math.cos(elevation),
    center.y + distance * Math.sin(elevation),
    center.z + distance * Math.cos(azimuth) * Math.cos(elevation)
  );

  camera.position.copy(cameraPosition);
  controls.target.copy(center);
  controls.update();

  return cameraPosition.clone();
}

const ThreeDViewer = forwardRef<HTMLDivElement, ThreeDViewerProps>(
  ({ glbUrl, onResetRef, onZoomInRef, onLoadComplete, onLoadError }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);

    // Merge forwarded ref with internal ref
    const setContainerRef = useCallback(
      (node: HTMLDivElement | null) => {
        (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      },
      [ref]
    );

    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const sceneRef = useRef<THREE.Scene | null>(null);
    const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
    const controlsRef = useRef<OrbitControls | null>(null);
    const frameIdRef = useRef<number>(0);
    const modelRef = useRef<THREE.Group | null>(null);
    const groundRef = useRef<THREE.Mesh | null>(null);
    const envMapRef = useRef<THREE.Texture | null>(null);
    const defaultCameraPositionRef = useRef<THREE.Vector3>(new THREE.Vector3(0, 5, 10));

    const [isModelLoading, setIsModelLoading] = useState(true);
    const [modelError, setModelError] = useState<string | null>(null);

    // Reset view using OrbitControls
    const resetView = useCallback(() => {
      const ctrl = controlsRef.current;
      const cam = cameraRef.current;
      if (!ctrl || !cam) return;
      cam.position.copy(defaultCameraPositionRef.current);
      ctrl.target.set(0, 0, 0);
      ctrl.update();
    }, []);

    // Zoom in using camera distance manipulation
    const zoomIn = useCallback(() => {
      const ctrl = controlsRef.current;
      const cam = cameraRef.current;
      if (!ctrl || !cam) return;
      _zoomByDistance(ctrl, cam, -0.3);
      ctrl.update();
    }, []);

    // Expose reset/zoom via refs for PreviewPanel buttons
    useEffect(() => {
      if (onResetRef) onResetRef.current = resetView;
      if (onZoomInRef) onZoomInRef.current = zoomIn;
    }, [onResetRef, onZoomInRef, resetView, zoomIn]);

    // Scene setup
    useEffect(() => {
      if (!containerRef.current) return;
      const container = containerRef.current;

      const scene = new THREE.Scene();
      // No scene.background — CSS gradient shows through transparent canvas
      sceneRef.current = scene;

      const camera = new THREE.PerspectiveCamera(
        45,
        container.clientWidth / container.clientHeight,
        0.1,
        100
      );
      cameraRef.current = camera;

      // Renderer with alpha for CSS gradient background
      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
      });
      renderer.setClearColor(0x000000, 0);
      renderer.setSize(container.clientWidth, container.clientHeight);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      container.appendChild(renderer.domElement);
      rendererRef.current = renderer;

      // --- Task 1: 3-point studio lighting ---

      // Ambient — low for more contrast
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.15);
      scene.add(ambientLight);

      // Key light — warm, upper-left, casts shadow
      const keyLight = new THREE.DirectionalLight(0xfff5e6, 1.2);
      keyLight.position.set(-3, 5, 5);
      keyLight.castShadow = true;
      keyLight.shadow.mapSize.set(1024, 1024);
      keyLight.shadow.bias = -0.001;
      keyLight.shadow.camera.near = 0.5;
      keyLight.shadow.camera.far = 20;
      keyLight.shadow.camera.left = -8;
      keyLight.shadow.camera.right = 8;
      keyLight.shadow.camera.top = 8;
      keyLight.shadow.camera.bottom = -8;
      scene.add(keyLight);

      // Fill light — cooler, opposite side
      const fillLight = new THREE.DirectionalLight(0xe6f0ff, 0.5);
      fillLight.position.set(4, 2, 3);
      scene.add(fillLight);

      // Rim light — behind and above, edge highlight
      const rimLight = new THREE.DirectionalLight(0xffffff, 0.8);
      rimLight.position.set(0, 4, -4);
      scene.add(rimLight);

      // --- Task 2: HDRI Environment Mapping (RoomEnvironment) ---
      const pmremGenerator = new THREE.PMREMGenerator(renderer);
      pmremGenerator.compileEquirectangularShader();
      const envMap = pmremGenerator.fromScene(new RoomEnvironment()).texture;
      scene.environment = envMap;
      pmremGenerator.dispose();
      envMapRef.current = envMap;

      // --- Task 4: OrbitControls ---
      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.enablePan = false;
      controls.rotateSpeed = 0.5;
      controls.zoomSpeed = 0.8;
      controls.minDistance = 5;
      controls.maxDistance = 15;
      controls.minPolarAngle = Math.PI / 6;
      controls.maxPolarAngle = Math.PI / 2.2;
      controls.touches = {
        ONE: THREE.TOUCH.ROTATE,
        TWO: THREE.TOUCH.DOLLY,
      };
      controlsRef.current = controls;

      // Set initial camera position
      camera.position.set(0, 5, 10);
      controls.update();

      // Cursor feedback
      container.style.cursor = 'grab';
      const onPointerDown = () => { container.style.cursor = 'grabbing'; };
      const onPointerUp = () => { container.style.cursor = 'grab'; };
      container.addEventListener('pointerdown', onPointerDown);
      window.addEventListener('pointerup', onPointerUp);

      // Animation loop
      const animate = () => {
        controls.update(); // Required for damping
        renderer.render(scene, camera);
        frameIdRef.current = requestAnimationFrame(animate);
      };
      animate();

      // Resize handling
      const handleResize = () => {
        const w = container.clientWidth;
        const h = container.clientHeight;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
      };
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(container);

      return () => {
        cancelAnimationFrame(frameIdRef.current);
        resizeObserver.disconnect();
        container.removeEventListener('pointerdown', onPointerDown);
        window.removeEventListener('pointerup', onPointerUp);

        controls.dispose();

        // Dispose ground plane
        if (groundRef.current) {
          groundRef.current.geometry.dispose();
          (groundRef.current.material as THREE.Material).dispose();
          groundRef.current = null;
        }

        // Dispose environment map
        if (envMapRef.current) {
          envMapRef.current.dispose();
          envMapRef.current = null;
        }

        // Dispose all scene children
        scene.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.geometry.dispose();
            const materials = Array.isArray(child.material)
              ? child.material
              : [child.material];
            materials.forEach(disposeMaterial);
          }
        });
        renderer.dispose();
        if (container.contains(renderer.domElement)) {
          container.removeChild(renderer.domElement);
        }
      };
    }, []);

    // Load GLB when URL changes
    const currentUrlRef = useRef<string | null>(null);

    useEffect(() => {
      const scene = sceneRef.current;
      const controls = controlsRef.current;
      const camera = cameraRef.current;
      if (!scene || !controls || !camera || !glbUrl) return;

      currentUrlRef.current = glbUrl;

      const disposeModel = (model: THREE.Group) => {
        scene.remove(model);
        model.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.geometry.dispose();
            const materials = Array.isArray(child.material)
              ? child.material
              : [child.material];
            materials.forEach(disposeMaterial);
          }
        });
      };

      // Remove previous model and ground
      if (modelRef.current) {
        disposeModel(modelRef.current);
        modelRef.current = null;
      }
      if (groundRef.current) {
        scene.remove(groundRef.current);
        groundRef.current.geometry.dispose();
        (groundRef.current.material as THREE.Material).dispose();
        groundRef.current = null;
      }

      // Validate GLB URL origin before loading
      if (!isAllowedGlbOrigin(glbUrl)) {
        setIsModelLoading(false);
        setModelError('Invalid preview URL');
        onLoadError?.('Invalid preview URL');
        return;
      }

      setIsModelLoading(true);
      setModelError(null);

      const loader = new GLTFLoader();
      loader.load(
        glbUrl,
        (gltf) => {
          // Stale URL guard
          if (currentUrlRef.current !== glbUrl) {
            gltf.scene.traverse((child) => {
              if (child instanceof THREE.Mesh) {
                child.geometry.dispose();
                const materials = Array.isArray(child.material)
                  ? child.material
                  : [child.material];
                materials.forEach(disposeMaterial);
              }
            });
            return;
          }

          const model = gltf.scene;

          // Center and scale model to fit view
          const box = new THREE.Box3().setFromObject(model);
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());
          const maxDim = Math.max(size.x, size.y, size.z);
          const scale = 6 / maxDim;

          model.position.sub(center);
          model.scale.setScalar(scale);
          model.position.y -= (size.y * scale) / 2 - 0.15;

          // Enable shadows on model meshes
          model.traverse((child) => {
            if (child instanceof THREE.Mesh) {
              child.castShadow = true;
              child.receiveShadow = true;
            }
          });

          scene.add(model);
          modelRef.current = model;

          // --- Task 1.3: Ground plane for contact shadow ---
          const modelBox = new THREE.Box3().setFromObject(model);
          const modelBottomY = modelBox.min.y;

          const groundGeometry = new THREE.PlaneGeometry(30, 30);
          const groundMaterial = new THREE.ShadowMaterial({
            opacity: 0.15,
            color: 0x000000,
          });
          const ground = new THREE.Mesh(groundGeometry, groundMaterial);
          ground.rotation.x = -Math.PI / 2;
          ground.position.y = modelBottomY - 0.01;
          ground.receiveShadow = true;
          scene.add(ground);
          groundRef.current = ground;

          // --- Task 5: Auto-frame camera ---
          const defaultPos = autoFrameModel(model, camera, controls);
          defaultCameraPositionRef.current = defaultPos;

          setIsModelLoading(false);
          onLoadComplete?.();
        },
        undefined,
        (error) => {
          if (currentUrlRef.current !== glbUrl) return;

          const is403 = String(error).includes('403');
          const errorMsg = is403
            ? 'Preview link expired. Please generate a new preview.'
            : "Couldn't load the 3D model";
          setIsModelLoading(false);
          setModelError(errorMsg);
          onLoadError?.(errorMsg);
        }
      );

      return () => {
        currentUrlRef.current = null;
      };
    }, [glbUrl, onLoadComplete, onLoadError]);

    // Keyboard navigation (works with OrbitControls)
    useEffect(() => {
      const container = containerRef.current;
      const controls = controlsRef.current;
      const camera = cameraRef.current;
      if (!container || !controls || !camera) return;

      const onKeyDown = (e: KeyboardEvent) => {
        const ROTATE_STEP = Math.PI / 12;

        switch (e.key) {
          case 'ArrowLeft':
            _rotateAzimuth(controls, camera, -ROTATE_STEP);
            controls.update();
            e.preventDefault();
            break;
          case 'ArrowRight':
            _rotateAzimuth(controls, camera, ROTATE_STEP);
            controls.update();
            e.preventDefault();
            break;
          case 'ArrowUp':
            _rotatePolar(controls, camera, -ROTATE_STEP);
            controls.update();
            e.preventDefault();
            break;
          case 'ArrowDown':
            _rotatePolar(controls, camera, ROTATE_STEP);
            controls.update();
            e.preventDefault();
            break;
          case '+':
          case '=':
            _zoomByDistance(controls, camera, -0.2);
            controls.update();
            e.preventDefault();
            break;
          case '-':
            _zoomByDistance(controls, camera, 0.2);
            controls.update();
            e.preventDefault();
            break;
          case 'Home':
            camera.position.copy(defaultCameraPositionRef.current);
            controls.target.set(0, 0, 0);
            controls.update();
            e.preventDefault();
            break;
        }
      };

      container.addEventListener('keydown', onKeyDown);
      return () => container.removeEventListener('keydown', onKeyDown);
    }, []);

    return (
      <div
        ref={setContainerRef}
        className="w-full h-full relative"
        style={{
          background: 'linear-gradient(180deg, #f5f2ed 0%, #e8e4de 60%, #ddd8d0 100%)',
        }}
        role="img"
        aria-label="Interactive 3D preview of your map"
        tabIndex={0}
      >
        {isModelLoading && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
          </div>
        )}
        {modelError && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <p className="text-sm text-red-500">{modelError}</p>
          </div>
        )}
      </div>
    );
  }
);

ThreeDViewer.displayName = 'ThreeDViewer';
export default ThreeDViewer;
